<script lang="ts">
  import { BALANCE, GAME_MODE, type MatchPhase, type MatchResult } from '@2k48/shared';
  import { audio } from '../../audio/AudioManager';
  import { animateOverlay, animateResult } from '../../game/motion';
  import {
    NetworkBattleController,
    type NetworkGameUiState,
  } from '../../game/network/NetworkBattleController';
  import { mountLocalGame, type LocalGameHandle } from '../../game/local/mountLocalGame';
  import { joinMatch, type MatchSession } from '../../net/client';
  import { isWithinPhaseHold } from '../../net/phases';
  import { feedback } from '../../platform/feedback';
  import BattleCountdown from '../components/BattleCountdown.svelte';
  import PixelButton from '../components/PixelButton.svelte';
  import PixelIcon from '../components/PixelIcon.svelte';
  import WaveText from '../components/WaveText.svelte';
  import { i18n } from '../i18n/index.svelte';
  import { router } from '../stores/router.svelte';
  import { session } from '../stores/session.svelte';
  import { announceAchievements, toasts } from '../stores/toasts.svelte';

  const t = $derived(i18n.t);

  let host: HTMLDivElement;
  let match: MatchSession | null = null;
  let ui = $state<NetworkGameUiState>({ players: [], localSeat: 0, modeId: '', roomCode: '' });
  let phase = $state<MatchPhase>('lobby');
  let remainingMs = $state(120_000);
  let countdownMs = $state<number>(BALANCE.phases.countdownMs);
  let result = $state<MatchResult | null>(null);
  let error = $state('');
  let reconnecting = $state(false);
  let rematchWaiting = $state(false);
  let goVisible = $state(false);
  let lastCountdownDigit: number | null = null;
  let announcedResultId = '';
  /** Bumped by the retry action on the error toast; re-runs the effect below. */
  let attempt = $state(0);

  const CONNECTION_TOAST = 'solo:connection';
  const SERVER_TOAST = 'solo:server';

  /**
   * True while this run is being played by the device instead of by a room.
   *
   * Solo is pure board rules, and `shared/` runs them identically on both
   * sides — so a missing server is a reason to play locally, not a reason to
   * show a dead end. What it costs is stated rather than hidden: see
   * `RunSummary.saved`.
   */
  let offline = $state(false);
  let localRun: LocalGameHandle | null = null;

  /**
   * What the results overlay needs, from whichever side produced the run.
   *
   * Deliberately not a `MatchResult`: that type is documented as "produced by a
   * GameMode, never by the client", and forging one here so the same overlay
   * could read it would be exactly the kind of lie that rule exists to prevent.
   * This is a view model, it never leaves the view, and it carries the one
   * field the authoritative shape has no reason to have.
   */
  interface RunSummary {
    score: number;
    maxTile: number;
    maxCombo: number;
    moves: number;
    /** False for an offline run: the server writes scores, and it never saw this one. */
    saved: boolean;
  }

  let offlineSummary = $state<RunSummary | null>(null);

  const endless = $derived(router.current.params.mode === 'endless');
  const player = $derived(ui.players.find((candidate) => candidate.seat === ui.localSeat));
  const localResult = $derived(result?.seats.find((seat) => seat.seat === ui.localSeat));
  const clock = $derived(endless ? '∞' : formatClock(remainingMs));

  const summary = $derived<RunSummary | null>(
    offlineSummary ??
      (localResult
        ? {
            score: localResult.score,
            maxTile: localResult.stats.maxTile ?? 0,
            maxCombo: localResult.stats.maxCombo ?? 0,
            moves: localResult.stats.moves ?? 0,
            saved: true,
          }
        : null),
  );

  $effect(() => {
    const next = result;
    if (phase !== 'ended' || !next || announcedResultId === next.matchId) return;
    const seat = next.seats.find((entry) => entry.seat === ui.localSeat);
    if (!seat) return;
    announcedResultId = next.matchId;
    if (seat.outcome === 'win') {
      audio.playMusic('victory');
      feedback.emit('match.victory', { audio: false });
    } else if (seat.outcome === 'loss') {
      audio.playMusic('defeat');
      feedback.emit('match.defeat', { audio: false });
    } else {
      audio.stopMusic();
    }
  });

  $effect(() => {
    void attempt;
    let cancelled = false;
    const controller = new NetworkBattleController(host, {
      onState: (state) => (ui = state),
      onPhase: (envelope) => {
        phase = envelope.phase;
        reconnecting = false;
        syncPhaseMusic(envelope.phase);
        if (envelope.phase === 'countdown') {
          countdownMs = BALANCE.phases.countdownMs;
          lastCountdownDigit = null;
        }
        if (envelope.phase === 'playing') {
          if (envelope.endsAt !== null) {
            remainingMs = Math.max(0, envelope.endsAt - envelope.serverTime);
            goVisible = isWithinPhaseHold(envelope, remainingMs, BALANCE.phases.goHoldMs);
          }
          feedback.emit('match.go');
        }
      },
      onProgress: (remaining, envelope) => {
        if (envelope.phase === 'countdown') {
          countdownMs = remaining;
          announceCountdownBeat(remaining);
        }
        if (envelope.phase === 'playing') {
          if (!endless) remainingMs = remaining;
          goVisible = isWithinPhaseHold(envelope, remaining, BALANCE.phases.goHoldMs);
        }
      },
      onResult: (next) => (result = next),
      onAchievements: (ids) => announceAchievements(ids),
      onReset: () => {
        result = null;
        rematchWaiting = false;
        announcedResultId = '';
      },
      onDrop: () => {
        reconnecting = true;
        toasts.warning(i18n.t.toasts.unstableConnection, { key: CONNECTION_TOAST });
      },
      onReconnect: () => {
        reconnecting = false;
        toasts.dismissKey(CONNECTION_TOAST);
      },
      onError: (reason) => announceFailure(reason),
    });
    const identity = {
      ...(session.profile?.id ? { playerId: session.profile.id } : {}),
      displayName: session.profile?.displayName ?? i18n.t.common.player,
    };

    void joinMatch(endless ? GAME_MODE.ENDLESS : GAME_MODE.SOLO, identity, controller.handlers)
      .then((connected) => {
        if (cancelled) {
          void connected.leave();
          return;
        }
        match = connected;
        controller.attach(connected);
      })
      .catch(() => {
        if (cancelled) return;
        // The room is what could not be reached, not the game. Hand the board
        // to the device and say so, rather than showing a wall.
        controller.destroy();
        startLocalRun();
      });

    return () => {
      cancelled = true;
      toasts.dismissKey(CONNECTION_TOAST);
      toasts.dismissKey(SERVER_TOAST);
      const leaving = match;
      match = null;
      controller.destroy();
      localRun?.destroy();
      localRun = null;
      if (leaving) void leaving.leave();
    };
  });

  /**
   * Play the run on this device.
   *
   * The rules are the same functions the room would have run — `LocalBattle`
   * drives `moveBoard`, `calculateDamage` and `recoverStalledBoard` out of
   * `shared/`, which is the whole reason that package is pure. What is missing
   * is the authority, and the only thing authority was buying here is the right
   * to persist a score.
   */
  function startLocalRun(): void {
    offline = true;
    error = '';
    phase = 'playing';
    goVisible = false;
    result = null;
    offlineSummary = null;
    audio.playMusic('battle');

    const roundMs = endless ? null : BALANCE.phases.scoreAttackRoundMs;
    remainingMs = roundMs ?? 0;

    localRun = mountLocalGame(host, {
      combat: false,
      roundMs,
      onState: (state) => {
        ui = {
          localSeat: 0,
          modeId: endless ? GAME_MODE.ENDLESS : GAME_MODE.SOLO,
          roomCode: '',
          players: [
            {
              seat: 0,
              name: session.profile?.displayName ?? i18n.t.common.player,
              hp: state.playerHp,
              score: state.score,
              combo: state.combo,
              maxCombo: state.maxCombo,
              maxTile: state.maxTile,
              connected: false,
            },
          ],
        };
        if (!endless) remainingMs = state.remainingMs;

        // Two ways a local run ends, matching the two the room enforces: the
        // clock, and hit points drained by repeated stalled boards.
        const finished = state.status === 'ko' || (roundMs !== null && state.remainingMs === 0);
        if (!finished || offlineSummary) return;

        offlineSummary = {
          score: state.score,
          maxTile: state.maxTile,
          maxCombo: state.maxCombo,
          moves: state.moves,
          saved: false,
        };
        phase = 'ended';
        audio.stopMusic();
        feedback.emit('match.defeat', { audio: false });
      },
    });

    toasts.warning(i18n.t.toasts.offlineRun, {
      key: SERVER_TOAST,
      action: {
        label: i18n.t.common.retry,
        run: () => {
          offline = false;
          attempt += 1;
        },
      },
    });
  }

  function announceFailure(reason: unknown): void {
    error = messageOf(reason);
    toasts.error(i18n.t.toasts.couldNotConnectServer, {
      key: SERVER_TOAST,
      action: {
        label: i18n.t.common.retry,
        run: () => {
          error = '';
          attempt += 1;
        },
      },
    });
  }

  function requestRematch(): void {
    // An offline run has no room to ask. Re-running the effect starts a fresh
    // attempt, which tries the server again first — so playing again is also
    // how a player who has come back into signal gets a run that counts.
    if (offline) {
      offline = false;
      attempt += 1;
      return;
    }
    match?.rematch();
    rematchWaiting = true;
  }

  function announceCountdownBeat(remaining: number): void {
    if (remaining > BALANCE.phases.countdownMs - BALANCE.phases.countdownReadyHoldMs) return;
    const digit = Math.min(3, Math.max(1, Math.ceil(remaining / 1000)));
    if (digit === lastCountdownDigit) return;
    lastCountdownDigit = digit;
    feedback.emit('match.countdown');
  }

  function syncPhaseMusic(nextPhase: MatchPhase): void {
    if (nextPhase === 'lobby' || nextPhase === 'countdown') audio.playMusic('match');
    else if (nextPhase === 'playing') audio.playMusic('battle');
    else if (nextPhase === 'ko') audio.stopMusic();
  }

  function formatClock(ms: number): string {
    const seconds = Math.max(0, Math.ceil(ms / 1000));
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }

  function messageOf(reason: unknown): string {
    return reason instanceof Error ? reason.message : String(reason);
  }
</script>

<div class="view solo">
  <header class="view__header">
    <button class="topbar__back" onclick={() => router.go('play')} aria-label="Volver">
      <PixelIcon name="back" size={28} />
    </button>
    <h1 class="view__title">{endless ? t.battle.endless : t.battle.timed}</h1>
    <strong class="solo__clock">{clock}</strong>
  </header>

  <div class="solo__hud">
    <div class="stat">
      <span class="stat__label">{t.common.points}</span>
      <span class="stat__value">{player?.score ?? 0}</span>
    </div>
    <div class="stat solo__stat--right">
      <span class="stat__label">{t.common.maxTile}</span>
      <span class="stat__value">{player?.maxTile ?? 0}</span>
    </div>
  </div>

  <div class="solo__stage" bind:this={host}>
    {#if (phase === 'lobby' || phase === 'countdown') && !error}
      <div class="battle-overlay battle-overlay--stage" use:animateOverlay>
        {#if phase === 'countdown'}
          <BattleCountdown remainingMs={countdownMs} />
        {:else}
          <strong>{t.battle.preparingBoard}</strong>
        {/if}
      </div>
    {:else if goVisible}
      <div
        class="battle-overlay battle-overlay--stage battle-overlay--clear"
        use:animateOverlay={{ emphasis: true }}
      >
        <div class="battle-overlay__go-row">
          <i class="battle-overlay__spark battle-overlay__spark--cyan"></i>
          <i class="battle-overlay__spark battle-overlay__spark--small"></i>
          <span class="battle-overlay__go">{t.battle.go}</span>
          <i class="battle-overlay__spark battle-overlay__spark--small"></i>
          <i class="battle-overlay__spark battle-overlay__spark--mag"></i>
        </div>
      </div>
    {:else if phase === 'ko' && result?.reason === 'timeout'}
      <div
        class="battle-overlay battle-overlay--stage battle-overlay--countdown"
        use:animateOverlay={{ emphasis: true }}
      >
        <WaveText
          as="strong"
          class="battle-overlay__go battle-result__title"
          text={t.battle.time}
        />
      </div>
    {/if}
  </div>

  <p class="solo__hint">
    {t.battle.swipeHint(player?.maxCombo ?? 0, player?.hp ?? 100)}
  </p>

  {#if reconnecting}
    <div class="battle-overlay battle-overlay--full battle-overlay--connection" use:animateOverlay>
      <PixelIcon name="warning" size={60} />
      <strong>{t.battle.reconnecting}</strong>
      <span>{t.battle.keepingYourGame}</span>
    </div>
  {/if}

  {#if error}
    <div class="battle-overlay battle-overlay--full battle-overlay--interactive" use:animateOverlay>
      <PixelIcon name="warning" size={60} />
      <strong>{t.battle.connectionRequired}</strong>
      <span class="battle-overlay__message">{error}</span>
      <PixelButton variant="ghost" onclick={() => router.go('play')}>{t.common.back}</PixelButton>
    </div>
  {:else if phase === 'ended' && summary}
    <div
      class="battle-overlay battle-overlay--full battle-overlay--interactive battle-result"
      use:animateResult
    >
      <span class="battle-overlay__eyebrow">{endless ? t.battle.gameOver : t.battle.time}</span>
      <WaveText
        as="strong"
        class="solo-result__score battle-result__title"
        text={String(summary.score)}
      />
      <div class="battle-result__stats">
        <span>{t.common.maxTile} <b>{summary.maxTile}</b></span>
        <span>{t.common.maxCombo} <b>x{summary.maxCombo}</b></span>
        <span>{t.common.moves} <b>{summary.moves}</b></span>
      </div>

      <!--
        Said plainly, on the screen showing the number, at the moment it would
        otherwise be assumed to have counted. Scores are written by the server
        from matches it refereed; this run had no referee, so it is a score and
        not a record, and the player deserves to know that before they go
        looking for it on a leaderboard.
      -->
      {#if !summary.saved}
        <p class="solo-result__unsaved">{t.battle.offlineNotSaved}</p>
      {/if}
      <PixelButton variant="p1" disabled={rematchWaiting} onclick={requestRematch}
        >{rematchWaiting ? t.battle.preparing : t.battle.playAgain}</PixelButton
      >
      <PixelButton variant="ghost" onclick={() => router.go('play')}
        >{t.common.modesMenu}</PixelButton
      >
    </div>
  {/if}
</div>
